import DOMPurify from 'dompurify'
import type { Config } from 'dompurify'
import { getSupportedLanguage } from 'src/config/language_helpers.ts'
import type { SupportedCodes } from 'src/config/language_helpers.ts'
import { i18n, loadLanguageAsync } from 'src/config/i18n.ts'
import moment, { switchMomentLocale } from '@/config/moment.ts'
import { notify } from '@kyvg/vue3-notification'
import type { NotificationsOptions } from '@kyvg/vue3-notification'
import cacheHttp, { setCacheHttpAxiosInstance } from 'src/store/vue-axios/cacheHttp.js'
import { switchVeeValidateLocale } from '@/config/setup/plugins/veeValidate/index.js'
import { hasSlotContent } from '@/config/services/emptySlotCheck.ts'
import type { Router } from 'vue-router'
import type { AxiosStatic } from 'axios'
import type { Store } from 'vuex'
import type { State } from '@/types/store/state.ts'
import type { App } from 'vue'

// TODO VUE 3: properly type vue-router params
type DefaultRouteReturn =
  | { name: typeof defaultRouteName; params?: undefined }
  | { name: typeof defaultOnboardingRouteName; params: { schoolUuid: string } };

type FileSize = 'Gigabyte' | 'GB' | 'Megabyte' | 'MB';

// TODO VUE 3: get all possible file types from the backend
type File = {
  [key: string]: any;
  extension?: string;
  size: number;
};

type DateType = Date | string | number;

type DateRange = {
  beginsAt: DateType;
  endsAt: DateType;
};

// Type definition for can helpers function
// TODO VUE 3: move this to 'src/config/setup/plugins/can/index.js' when its an .ts file
type CanHelpersFunction = {
  (verb: string, instance: { can: Record<string, number> }): boolean;
  not: (verb: string, instance: { can: Record<string, number> }) => boolean;
};

// using a queue here to ensure that all calls of the switchLocale function are executed sequentially and to prevent race conditions
let languageSwitchQueue = Promise.resolve()

const overLaps = (beginsAt1: DateType, endsAt1: DateType, beginsAt2: DateType, endsAt2: DateType): boolean => {
  if (!beginsAt1 || !endsAt1 || !beginsAt2 || !endsAt2) return false
  return moment(beginsAt1).isSameOrBefore(endsAt2) && moment(endsAt1).isSameOrAfter(beginsAt2)
}
const sortObject = <T>(object: T): T => {
  if (!object) return object

  const isObject = object instanceof Object
  const isArray = object instanceof Array
  const isPrimitiveArray = isArray && object.length && !(object[0] instanceof Object)

  if (!isObject && !isArray) return object

  if (isPrimitiveArray) return [...object].sort() as T

  if (isArray) return object.map((item) => sortObject(item)) as T

  const sortedObj: Record<string, unknown> = {}
  const keys = Object.keys(object as Record<string, unknown>)

  keys.sort(function (key1, key2) {
    const lowerKey1 = key1.toLowerCase()
    const lowerKey2 = key2.toLowerCase()
    if (lowerKey1 < lowerKey2) return -1
    if (lowerKey1 > lowerKey2) return 1
    return 0
  })

  for (const key of keys) {
    const value = (object as Record<string, unknown>)[key]
    if (typeof value === 'object' && value !== null) {
      sortedObj[key] = sortObject(value)
    } else {
      sortedObj[key] = value
    }
  }

  return sortedObj as T
}

const defaultRouteName = 'feed' as const
const defaultOnboardingRouteName = 'onboarding-start' as const

const getSizePostFix = (inGigabytes = false): FileSize => {
  // TODO VUE 3: properly type the store
  const isScreenReaderActive = GlobalPrototypes.store?.state?.isScreenReaderActive ?? false
  if (inGigabytes) return isScreenReaderActive ? 'Gigabyte' : 'GB'
  return isScreenReaderActive ? 'Megabyte' : 'MB'
}

const GlobalPrototypes = {
  onboardingDefaultRoute() {
    return defaultOnboardingRouteName
  },
  defaultRoute(): DefaultRouteReturn {
    // TODO VUE 3: properly type the store
    const me = GlobalPrototypes.store?.getters?.['users/me']
    if (!me) return { name: defaultRouteName }

    if (me.school.state === 'ONBOARDING') {
      return {
        name: defaultOnboardingRouteName,
        params: { schoolUuid: me.school.uuid },
      }
    }

    return { name: defaultRouteName }
  },
  dateFormat: {
    // Default date and time formats
    date_time: (date: moment.MomentInput) => moment(date).format('DD.MM.YY HH:mm'), // SettingsProfile, ClassbookHomeworkListItem, AdminTimetableWebInterfaceStatus, BulkAction,  SchaduledNews, SettingsProfile, DuplicatedUsersParentModal, AdminTimetableWebInterfaceStatus.vue, SettingsSecurity,  ClassbookNotice, ClassbookNoticeHistory, ClassbookNoticeListItem, ClassbookTopicHistory, WriteMessageWrapper.vue, WriteMessageWrapper,
    date_without_year_time: (date: moment.MomentInput) => moment(date).format('DD.MM. HH:mm'), // Exports
    // CloudView, CloudDetailsView, SduiContentCard, ClassbookNoticeHistory,    TimetablePage, CloudAction, DuplicatedUsers, CloudDetailsView, TimetablePage, CloudAction, DuplicatedUsers, CloudDetailsView */
    date: (date: moment.MomentInput) => moment(date).format('DD.MM.YY'), // SettingsProfile, SduiListEntity, ClassbookHomework, SduiUserListItem,  SettingsSecurity, ClassbookHomeworkListItem, ClassbookHomeworkDueDate, FeedbackDialogPredictedSolution
    hour_minute: (date: moment.MomentInput) => moment(date).format('HH:mm'), // SduiMessageHint, SduiMessage, OverlapsInfo, TimeManagementForm.vue, SettingsSecurity, Timetable
    date_daylong_dayshort_monthlong_yearlong: (date: moment.MomentInput) => moment(date).format('dddd, DD. MMMM YYYY'), // calendar
    date_daylong_hour_minute: (date: moment.MomentInput) => moment(date).format('dddd HH:mm'),
    // Long year (DC mostly)
    date_yearlong: (date: moment.MomentInput) => moment(date).format('DD.MM.YYYY'), // Intro, Sdui.vue,
    date_monthshort: (date: moment.MomentInput) => moment(date).format('DD. MMM'), // Notification Centre List Item
    date_monthshort_yearlong: (date: moment.MomentInput) => moment(date).format('DD. MMM YYYY'), // SchoolContract, UserAbsence, Classbook, ClassList

    // special cases for message timetable, monitor and DC
    date_month_long: (date: moment.MomentInput) => moment(date).format('DD MMMM'), // SduiMessage
    weekday_date_month: (date: moment.MomentInput) => moment(date).format('dddd, DD MMM'), // ClassbookHomeworkDueDate, CourseDetails
    weekday_date_month_hour_minute: (date: moment.MomentInput) => moment(date).format('dddd, DD MMM, HH:mm'), // ClassbookHomeworkDueDate, CourseDetails
    weekday_date_month_year: (date: moment.MomentInput) => moment(date).format('dd. DD.MM.YY'), // MonitorHeader
    dateshort_monthshort: (date: moment.MomentInput) => moment(date).format('D MMM'), // SduiDateTimePicker
    weekdayshort_date_month: (date: moment.MomentInput) => moment(date).format('dd, DD.MM'), // MonitorSubstitutionHeader, MonitorSubstitutionView, OverlapsInfo
    date_dayshort_monthshort: (date: moment.MomentInput) => moment(date).format('DD.MM.'),
    weekdayshort_date_monthshort_yearshort: (date: moment.MomentInput) => moment(date).format('dd., DD. MMM YYYY'), //

    // special cases - year, week, month etc
    week: (date: moment.MomentInput) => moment(date).format('w'), // timetableRendering
    week_iso: (date: moment.MomentInput) => moment(date).format('W'), // CourseSelection
    weekdaylong: (date: moment.MomentInput) => moment(date).format('dddd'),
    yearlong: (date: moment.MomentInput) => moment(date).format('YYYY'), // TimetableProjectSwitch, timetable_meta.js
    weekday_short: (date: moment.MomentInput) => moment(date).format('dd'), // DateHeader
    date_day: (date: moment.MomentInput) => moment(date).format('DD'), // DateHeader
    month_short: (date: moment.MomentInput) => moment(date).format('MMM'), // DateHeader

    // not shown but used for calcultions or sent to backend
    timestamp: (date: moment.MomentInput) => moment(date).format('YYYY-MM-DD HH:mm:ss'), // ManagementSettings, users.js, sduiDateRangePicker
    timestamp_minute: (date: moment.MomentInput) => moment(date).format('YYYY-MM-DD HH:mm'), // ManagementSettings, users.js, sduiDateRangePicker
    yearlong_month_date: (date: moment.MomentInput) => moment(date).format('YYYY-MM-DD'), // ClassbookHomework
    date_empty_format: (date: moment.MomentInput) => moment(date).format(), // "2014-09-08T08:02:17-05:00" (ISO 8601, no fractional seconds) // ClassbookHomework,
    // localized formats
    l_lts: (date: moment.MomentInput) => moment(date).format('l LTS'), // Feed
    minute_seconds: (date: moment.MomentInput) => moment(date).format('mm:ss'),

    // e.g. "2 hours, 34 minutes, 1 second",  "1 hour, 1 minute, 30 seconds", etc
    humanReadableTimeLeft: (ms: number): string | undefined => {
      if (!ms) return undefined

      const countdownDuration = moment.duration(ms / 1000, 'seconds')
      const hours = countdownDuration.hours()
      const minutes = countdownDuration.minutes()
      const seconds = countdownDuration.seconds()

      const formattedDuration = []
      if (hours > 0) formattedDuration.push(`${hours} ${i18n.global.t('accessibility.hours', hours <= 1 ? 1 : 2)}`)
      if (minutes > 0) formattedDuration.push(`${minutes} ${i18n.global.t('accessibility.minutes', minutes <= 1 ? 1 : 2)}`)
      if (seconds > 0) formattedDuration.push(`${seconds} ${i18n.global.t('accessibility.seconds', seconds <= 1 ? 1 : 2)}`)
      return formattedDuration.join(', ')
    },
  },
  compare(obj1: object, obj2: object): boolean {
    const o1 = sortObject(obj1)
    const o2 = sortObject(obj2)

    return JSON.stringify(o1) === JSON.stringify(o2)
  },
  overlappingDateRanges(objArr1: DateRange[], objArr2: DateRange[]): boolean {
    if (!objArr1?.length || !objArr2?.length || objArr1.length !== objArr2.length) return false
    const length = objArr1.length
    for (let i = 0; i < length - 1; i += 1) {
      for (let j = i + 1; j < length; j += 1) {
        if (overLaps(objArr1[i].beginsAt, objArr1[i].endsAt, objArr2[j].beginsAt, objArr2[j].endsAt)) return true
      }
    }
    return false
  },
  clone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj))
  },
  uuid: (): string => {
    // if crypto is needed in more places move this to package, import in main.js
    const cryptoObj = window.crypto || window.msCrypto // for IE 11
    return ([1e7].toString() + (-1e3).toString() + (-4e3).toString() + (-8e3).toString() + (-1e11).toString()).replace(
      /[018]/g,
      (c: string) => (parseInt(c) ^ (cryptoObj.getRandomValues(new Uint8Array(1))[0] & (15 >> (parseInt(c) / 4)))).toString(16),
    )
  },
  sanitize: (html: string | Node, options: Config = {}): string => {
    // always allow attribute "target" - the "afterSanitizeAttributes"-hook is making sure its secure
    return DOMPurify.sanitize(html, { ...options, ADD_ATTR: ['target'] })
  },

  /**
   * Convert machine size to human format (megabyte or gigabyte)
   * Will use abbreviation "MB" or "GB" if screen reader is not active
   */
  getSize: function (sizeInKB: number | null | undefined, inGigabytes = false): string {
    const postfix = getSizePostFix(inGigabytes)

    if (!sizeInKB) return `0.00 ${postfix}`

    const size = inGigabytes ? sizeInKB / 1024 / 1024 / 1024 : sizeInKB / 1024 / 1024

    const finalSize = size > 0 ? size.toFixed(2) : '0.01'
    return `${finalSize} ${postfix}`
  },
  /**
   * Return formatted file extension and size in parentheses for accessibility
   * @param  {Object} file object with .extension property
   * @return {String} File extension and size in parentheses
   */
  getFileTypeAndSize: function (file: File | null | undefined): string {
    if (!file || !file.size) return ''
    if (!file.extension) return `(${GlobalPrototypes.getSize(file.size, false)})` // handling files w/o extension
    return `(${file.extension.toUpperCase()}, ${GlobalPrototypes.getSize(file.size, false)})`
  },
  /**
   * Return formatted folder size in parentheses for accessibility
   * @param files
   * @returns {string}
   */
  getFolderSize: function (files: File[]): string {
    if (!files?.length) return `0.00 ${getSizePostFix(false)}`

    // get total size of all files in folder
    const totalSize = files.reduce((acc, file) => acc + file.size, 0)

    // TODO backend doesnt return the file sizes, when a folder is requested
    if (!totalSize) return ''

    return `(${GlobalPrototypes.getSize(totalSize, false)})`
  },
  /**
   * Return formatted file extension and size in parentheses for accessibility
   * @param  {Object} files objects with .extension property
   * @return {String} File extension and size in parentheses
   */
  getFileTypeAndFolderSize: function (files: File[]): string {
    // folders/directories are always downloaded as zip files
    return `(ZIP, ${GlobalPrototypes.getFolderSize(files).replace('(', '').replace(')', '')})`
  },
  sleep: (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms))
  },
  switchLocale: async function (lang?: SupportedCodes): Promise<void> {
    // in cypress tests we need to set the language statically to german, to have reliable results on all system
    // but in cases where we set language for a seeded user we still need to respect this language
    const languageSwitchPromise = async () => {
      const overrideWithDefaultLang = window.Cypress && !lang

      const locales = lang ? [lang] : navigator.languages || [navigator.language]
      const { origin } = getSupportedLanguage(overrideWithDefaultLang ? ['de'] : Array.from(locales))

      await loadLanguageAsync(origin)

      // @ts-expect-error TODO VUE 3: vee-i18n add `.value` at the end of when legacy API is removed. Until then, ignore typescript error
      i18n.global.locale = origin

      await GlobalPrototypes.store?.dispatch('schoolBranding/loadLanguageVariables')

      switchMomentLocale(origin)

      await switchVeeValidateLocale(origin)
    }
    languageSwitchQueue = languageSwitchQueue.then(languageSwitchPromise)

    return languageSwitchQueue
  },
  getCurrentLocale: () => {
    // TODO VUE 3: vee-i18n add `.value` at the end of when legacy API is removed
    return i18n.global.locale || 'de'
  },
  toastNotify: async function (params: NotificationsOptions): Promise<void> {
    // Delaying notifications ensures that screen readers can announce them correctly. Most actions that spawn
    // a notification are doing some focus changes afterwards. To not interrupt SR, we delay it.
    if (GlobalPrototypes.store?.state?.isScreenReaderActive) {
      await GlobalPrototypes.sleep(1500)
    }

    notify(params)
  },
  isA11yDemoSchool: () => {
    const demoSchools = import.meta.env.VITE_SLINK_DEMO_ACCESSIBILITY_SCHOOL.split(',')
    return demoSchools.includes(GlobalPrototypes.store?.getters?.['users/me']?.school?.slink)
  },
  hasSlotContent: hasSlotContent,
  moment: moment,
  cacheHttp: cacheHttp,
  axios: null as AxiosStatic | null,
  store: null as Store<State> | null,
  router: null as Router | null,
  can: null as CanHelpersFunction | null,
}

export const setGlobalAxiosInstance = (axiosInstance: AxiosStatic): void => {
  if (!axiosInstance || !axiosInstance.defaults) {
    console.error('[Global prop initialisation] Invalid axios instance provided')
    return
  }
  if (GlobalPrototypes.axios && import.meta.env.NODE_ENV !== 'test') {
    console.warn('[Global prop initialisation] Axios instance already set, ignoring new instance')
    return
  }
  GlobalPrototypes.axios = axiosInstance
  setCacheHttpAxiosInstance(axiosInstance)
}

export const setGlobalStoreInstance = (storeInstance: Store<State>): void => {
  if (!storeInstance) {
    console.error('[Global prop initialisation] Invalid store instance provided')
    return
  }
  if (GlobalPrototypes.store && import.meta.env.NODE_ENV !== 'test') {
    console.warn('[Global prop initialisation] Store instance already set, ignoring new instance')
    return
  }
  GlobalPrototypes.store = storeInstance
}

export const setGlobalRouterInstance = (routerInstance: Router): void => {
  if (!routerInstance) {
    console.error('[Global prop initialisation] Invalid router instance provided')
    return
  }
  if (GlobalPrototypes.router && import.meta.env.NODE_ENV !== 'test') {
    console.warn('[Global prop initialisation] Router instance already set, ignoring new instance')
    return
  }
  GlobalPrototypes.router = routerInstance
}

export const setGlobalCanHelpers = (app: App, canHelpers: CanHelpersFunction): void => {
  if (!canHelpers || typeof canHelpers !== 'function') {
    console.error('[Global prop initialisation] Invalid can helpers provided')
    return
  }
  if (GlobalPrototypes.can && import.meta.env.NODE_ENV !== 'test') {
    console.warn('[Global prop initialisation] Can helpers already set, ignoring new instance')
    return
  }
  app.config.globalProperties.$can = canHelpers
  GlobalPrototypes.can = canHelpers
}

export default GlobalPrototypes
